import type { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Button } from "./button";
import { cn } from "@/lib/utils";
import { useDialogLoadingGuard, getMaxWidthClass } from "./dialog-utils";
import type { MaxWidth } from "./dialog-utils";

interface DialogButton {
  label: string;
  loadingText?: string;
  onClick: () => void | Promise<void>;
  variant?: "default" | "secondary" | "destructive";
  type?: "button" | "submit";
  disabled?: boolean;
  isLoading?: boolean;
}

interface DetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  icon?: ReactNode;
  showCloseButton?: boolean;
  maxWidth?: MaxWidth;
  buttons?: DialogButton[];
  bodyClassName?: string;
  contentClassName?: string;
  headerClassName?: string;
  loading?: boolean;
}

export const DetailDialog = ({
  open,
  onOpenChange,
  title,
  subtitle,
  children,
  icon,
  showCloseButton = true,
  maxWidth = "md",
  buttons,
  bodyClassName = "p-4 sm:p-6",
  contentClassName,
  headerClassName,
  loading = false,
}: DetailDialogProps) => {
  const { handleOpenChange } = useDialogLoadingGuard(onOpenChange, loading);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={showCloseButton}
        className={cn(getMaxWidthClass(maxWidth), contentClassName)}
      >
        <DialogHeader className={headerClassName}>
          <div className="flex items-center gap-3">
            {icon && <div className="shrink-0">{icon}</div>}
            <DialogTitle>{title}</DialogTitle>
          </div>
          {subtitle && (
            <DialogDescription>
              <span>{subtitle}</span>
            </DialogDescription>
          )}
        </DialogHeader>

        <div
          className={cn(
            "flex-1 min-h-0 overflow-y-auto",
            bodyClassName,
          )}
        >
          {children}
        </div>

        {buttons && buttons.length > 0 && (
          <DialogFooter>
            {buttons.map((btn, index) => (
              <Button
                key={index}
                type={btn.type || "button"}
                variant={btn.variant || "default"}
                className="flex-1"
                disabled={btn.disabled || loading}
                isLoading={btn.isLoading ?? false}
                onClick={btn.onClick}
              >
                {btn.isLoading ? (btn.loadingText ?? btn.label) : btn.label}
              </Button>
            ))}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export type { DialogButton };
